import { useEffect } from 'react';

export const input =
  'mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive';
export const label = 'block text-sm font-medium text-neutral-700';

/** Styled native file input for admin uploads */
export const fileInput =
  'mt-1 block w-full cursor-pointer text-sm text-neutral-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-neutral-100 file:px-4 file:py-2.5 file:text-xs file:font-semibold file:text-neutral-800 file:transition-colors hover:file:bg-neutral-200';

/** Compact thumbnail for uploaded / pasted image URLs in edit forms */
/** Full-screen overlay dialog for admin create/edit flows */
export function AdminModal({ open, title, description, error, onClose, children, footer, panelRef }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="relative flex max-h-[min(92dvh,880px)] w-full max-w-2xl flex-col rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[min(85vh,880px)] sm:rounded-2xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0 pr-2">
            <h2 id="admin-modal-title" className="text-base font-bold text-neutral-900 sm:text-lg">
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-neutral-600">{description}</p> : null}
            {error ? (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-neutral-200 bg-neutral-50/90 px-4 py-3 sm:px-6 sm:py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function AdminImagePreview({ src, alt = '', emptyLabel = 'No image yet' }) {
  return (
    <div className="mb-6">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">Preview</p>
      <div className="relative inline-block aspect-[4/3] w-36 max-w-full overflow-hidden rounded-xl border border-neutral-200 bg-gradient-to-br from-neutral-100 to-neutral-200 sm:w-44">
        {src ? (
          <img src={src} alt={alt || ''} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-2 text-center text-[11px] leading-snug text-neutral-400">
            {emptyLabel}
          </div>
        )}
      </div>
    </div>
  );
}

export function emptyArticle() {
  return {
    id: `n-${crypto.randomUUID()}`,
    date: '',
    dateIso: '',
    title: '',
    excerpt: '',
    body: '',
    image: '',
    alt: '',
    readMoreHref: '#contact',
  };
}

export function emptyProduct() {
  return {
    id: `p-${crypto.randomUUID()}`,
    date: '',
    dateIso: '',
    title: '',
    description: '',
    image: '',
    alt: '',
  };
}

export function emptyGalleryItem() {
  return {
    id: `g-${crypto.randomUUID()}`,
    src: '',
    alt: '',
    title: '',
    album: '',
  };
}

export function navButtonClass(active) {
  return [
    'group relative block w-full rounded-xl px-3 py-2.5 text-left text-sm transition-all duration-150',
    active
      ? 'bg-gradient-to-r from-brand-forest to-[#28523d] text-white shadow-sm ring-1 ring-brand-forest/40'
      : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 hover:shadow-sm',
  ].join(' ');
}

export function mobileTabClass(active) {
  return [
    'inline-flex shrink-0 items-center rounded-full border px-3 py-1.5 text-center text-xs font-semibold transition-colors',
    active
      ? 'border-brand-forest bg-brand-forest text-white shadow-sm'
      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
  ].join(' ');
}
